<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  preview: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const { t } = useI18n();

const dialogRef = ref(null);

const inboxes = computed(() => props.preview?.inboxes ?? []);
const totals = computed(() => props.preview?.totals ?? { eligible: 0, skipped: 0 });
const cost = computed(() => props.preview?.cost_estimate);

// Labels that match nobody and labels that match unreachable people look the same
// in the totals, so the first case gets its own message.
const noContactsMatched = computed(
  () => props.preview != null && props.preview.matched_contacts === 0
);

const reasonLabel = reason =>
  t(`MARKETING.BROADCASTS.SKIP_REASONS.${reason.toUpperCase()}`);

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('MARKETING.BROADCASTS.PREVIEW.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="t('MARKETING.BROADCASTS.PREVIEW.CLOSE')"
  >
    <div v-if="isLoading" class="py-6 text-sm text-n-slate-11">
      {{ t('MARKETING.BROADCASTS.PREVIEW.LOADING') }}
    </div>
    <div v-else class="flex flex-col gap-4">
      <p
        v-if="noContactsMatched"
        class="p-3 text-sm rounded-lg bg-n-alpha-2 text-n-amber-11"
      >
        {{ t('MARKETING.BROADCASTS.PREVIEW.NO_CONTACTS_MATCHED') }}
      </p>

      <div class="flex gap-6 text-sm">
        <span class="text-n-teal-11">
          {{ t('MARKETING.BROADCASTS.PREVIEW.ELIGIBLE') }}:
          <strong>{{ totals.eligible }}</strong>
        </span>
        <span class="text-n-slate-11">
          {{ t('MARKETING.BROADCASTS.PREVIEW.SKIPPED') }}:
          <strong>{{ totals.skipped }}</strong>
        </span>
      </div>

      <div
        v-for="inbox in inboxes"
        :key="inbox.id"
        class="flex flex-col gap-1 p-3 rounded-lg border border-n-weak"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ inbox.name }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              inbox.billable
                ? t('MARKETING.BROADCASTS.PREVIEW.BILLABLE')
                : t('MARKETING.BROADCASTS.PREVIEW.FREE')
            }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11">
          {{ t('MARKETING.BROADCASTS.PREVIEW.ELIGIBLE') }}: {{ inbox.eligible }}
          ·
          {{ t('MARKETING.BROADCASTS.PREVIEW.SKIPPED') }}: {{ inbox.skipped }}
        </span>
        <ul
          v-if="Object.keys(inbox.skipped_by_reason || {}).length"
          class="mt-1 text-xs text-n-slate-11 list-disc ltr:pl-4 rtl:pr-4"
        >
          <li v-for="(count, reason) in inbox.skipped_by_reason" :key="reason">
            {{ reasonLabel(reason) }}: {{ count }}
          </li>
        </ul>
      </div>

      <div
        v-if="cost"
        class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2 text-sm"
      >
        <span class="font-medium text-n-slate-12">
          {{ t('MARKETING.BROADCASTS.PREVIEW.COST_TITLE') }}
        </span>
        <span class="text-n-slate-11">
          {{ cost.billable_messages }} ×
          {{ cost.rate_per_message }} {{ cost.currency }} =
          <strong>{{ cost.total }} {{ cost.currency }}</strong>
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('MARKETING.BROADCASTS.PREVIEW.COST_DISCLAIMER') }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
